import React from 'react';
import uuidv4 from 'uuid/v4';
import gql from 'graphql-tag';
import { useStoreState } from 'easy-peasy';
import client from '../../../utils/apolloClient';

const searchUserLoginHistory = gql`
  query searchUserLoginHistory(
    $userId: String
    $sort: String
    $from: Int
    $size: Int
  ) {
    search_user_login_history(
      input: {
        filter: { user_filter: { user_id: [$userId] } }
        sort: $sort
        from: $from
        size: $size
      }
    ) {
      total_pages
      total_size
      user_login_history {
        logged_in
        login_at
        last_login
        user_type
        login_type
        app_type
        version_number
        build_number
        platform
        web_browser
        device_name
        model_number
        ip_address
        unique_id
      }
    }
  }
`;

export const fetchUserLoginHistory = async ({ userId, sort, from, size }) => {
  try {
    const data = await client.clientPrivate.query({
      client: client.clientPrivate,
      query: searchUserLoginHistory,
      variables: { userId, sort, from, size },
      fetchPolicy: 'network-only',
    });
    if (data && data.data && data.data.search_user_login_history) {
      return data.data.search_user_login_history;
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

const fetchStripeBlockUserQuery = gql`
  query fetchStripeBlockUserQuery($stripeBlockUserId: String) {
    fetch_stripe_block_users(
      input: { stripe_block_user_id: $stripeBlockUserId }
    ) {
      device_ids
      payment_method_id
      stripe_block_user_id
      user_id
      email_ids
      phone_numbers
      error {
        code
        description
        object {
          id
          name
          type
        }
      }
    }
  }
`;

export const fetchStripeBlockUser = async ({ stripeBlockUserId }) => {
  try {
    const data = await client.clientPrivate.query({
      client: client.clientPrivate,
      query: fetchStripeBlockUserQuery,
      variables: {
        stripeBlockUserId,
      },
      fetchPolicy: 'network-only',
    });

    if (data && data?.data?.fetch_stripe_block_users) {
      return data?.data?.fetch_stripe_block_users;
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

const stripeBlockUserListingQuery = gql`
  query search_stripe_block_users($input: SearchInput) {
    search_stripe_block_users(input: $input) {
      stripe_block_user_listing {
        device_ids
        payment_method_id
        stripe_block_user_id
        user_id
        email_ids
        phone_numbers
        error {
          code
          description
          object {
            id
            name
            type
          }
        }
      }
      total_pages
      total_size
    }
  }
`;

export const stripeBlockUserListing = async ({ input }) => {
  try {
    const data = await client.clientPrivate.query({
      client: client.clientPrivate,
      query: stripeBlockUserListingQuery,
      variables: {
        input,
      },
      fetchPolicy: 'network-only',
    });

    if (data?.data?.search_stripe_block_users?.stripe_block_user_listing?.[0]) {
      return data?.data?.search_stripe_block_users
        ?.stripe_block_user_listing?.[0];
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const useStripeBlockUser = ({ stripeBlockUserId, email }) => {
  const { userId } = useStoreState(state => state.auth);

  const [loading, setLoading] = React.useState(true);
  const [stripeBlockUser, setStripeBlockUser] = React.useState(null);

  const [reload, setReload] = React.useState(uuidv4());

  const refreshDataFunc = () => setReload(uuidv4());

  const stripeBlockUserListingInput = {};
  const stripe_block_user_filter = {};
  Object.assign(stripe_block_user_filter, email && { email_id: email });
  Object.assign(
    stripeBlockUserListingInput,
    { user_id: userId },
    { filter: { stripe_block_user_filter } },
  );

  React.useEffect(() => {
    const fetchFunc = async () => {
      setLoading(true);
      setStripeBlockUser(null);

      if (stripeBlockUserId) {
        const res = await fetchStripeBlockUser({ stripeBlockUserId });

        if (res) {
          setStripeBlockUser(res);
        } else {
          // pass
        }
      }

      if (email) {
        const res = await stripeBlockUserListing({
          input: stripeBlockUserListingInput,
        });

        if (res) {
          setStripeBlockUser(res);
        } else {
          // pass
        }
      }
      setLoading(false);
    };

    fetchFunc();
  }, [stripeBlockUserId, reload, email]);

  return [loading, stripeBlockUser, refreshDataFunc];
};

const createStripeBlockUsersMutation = gql`
  mutation createStripeBlockUsersMutation(
    $stripeBlockUserId: String
    $userId: String
    $isBlocked: Boolean
    $deviceId: [String]
    $emailIds: [String]
    $phoneNumbers: [String]
  ) {
    create_stripe_block_users(
      input: {
        stripe_block_user_id: $stripeBlockUserId
        user_id: $userId
        is_blocked: $isBlocked
        device_ids: $deviceId
        email_ids: $emailIds
        phone_numbers: $phoneNumbers
      }
    ) {
      device_ids
      payment_method_id
      stripe_block_user_id
      user_id
    }
  }
`;

const updateStripeBlockUsersMutation = gql`
  mutation updateStripeBlockUsersMutation(
    $stripeBlockUserId: String
    $userId: String
    $isBlocked: Boolean
    $deviceId: [String]
    $emailIds: [String]
    $phoneNumbers: [String]
  ) {
    update_stripe_block_users(
      input: {
        stripe_block_user_id: $stripeBlockUserId
        user_id: $userId
        is_blocked: $isBlocked
        device_ids: $deviceId
        email_ids: $emailIds
        phone_numbers: $phoneNumbers
      }
    ) {
      device_ids
      payment_method_id
      stripe_block_user_id
      user_id
    }
  }
`;

const deleteStripeBlockUsersMutation = gql`
  mutation deleteStripeBlockUsersMutation($stripeBlockUserId: String) {
    delete_stripe_block_users(
      input: { stripe_block_user_id: $stripeBlockUserId }
    ) {
      device_ids
      payment_method_id
      stripe_block_user_id
      user_id
    }
  }
`;

const mutateBlockUser = async ({ input, mutation }) => {
  try {
    await client.clientPrivate.mutate({
      client: client.clientPrivate,
      mutation,
      variables: {
        ...input,
      },
    });
    // pass
  } catch {
    // pass
  }
};

export const onUpdateStripeBlockUser = async ({
  stripeBlockUserId,
  userId,
  isSelected,
  isUpdate,
  refreshData,
  deviceId,
  emailIds,
  phoneNumbers,
}) => {
  if (isUpdate && isSelected) {
    await mutateBlockUser({
      input: {
        stripeBlockUserId,
        userId,
        isBlocked: isSelected,
        deviceId,
        phoneNumbers,
        emailIds,
      },
      mutation: updateStripeBlockUsersMutation,
    });
    refreshData();
  } else if (isUpdate && !isSelected) {
    await mutateBlockUser({
      input: { stripeBlockUserId, deviceId },
      mutation: deleteStripeBlockUsersMutation,
    });
    refreshData();
  } else if (!isUpdate && isSelected) {
    await mutateBlockUser({
      input: {
        stripeBlockUserId,
        userId,
        isBlocked: isSelected,
        deviceId,
        phoneNumbers,
        emailIds,
      },
      mutation: createStripeBlockUsersMutation,
    });
    refreshData();
  } else if (!isUpdate && !isSelected) {
    // pass
  } else {
    // pass
  }
};

export default {};
